import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminOrders, useMarkOrderDelivered, useCancelOrder } from '@/hooks/useAdminOrders';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/ui/search-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CheckCircle, XCircle, Eye, FileText } from 'lucide-react';
import { format } from 'date-fns';

const AdminOrders = () => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState({
    status: '',
    search: '',
  });
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [actionDialog, setActionDialog] = useState<{ type: 'deliver' | 'cancel' | null; order: any }>({ type: null, order: null });
  
  const { data: orders, isLoading } = useAdminOrders(filters);
  const { mutate: markDelivered } = useMarkOrderDelivered();
  const { mutate: cancelOrder } = useCancelOrder();

  const filteredOrders = orders?.filter(order => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        order.order_number.toLowerCase().includes(searchLower) ||
        order.shipper_company?.name.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const handleAction = () => {
    if (!actionDialog.order) return;
    
    switch (actionDialog.type) {
      case 'deliver':
        markDelivered(actionDialog.order.id);
        break;
      case 'cancel':
        cancelOrder(actionDialog.order.id);
        break;
    }
    setActionDialog({ type: null, order: null });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; label: string }> = {
      draft: { color: 'bg-[#87ADD4]', label: t('orders.status.draft') },
      posted: { color: 'bg-[#0880AE]', label: t('orders.status.posted') },
      booked: { color: 'bg-[#F2AC57]', label: t('orders.status.booked') },
      assigned: { color: 'bg-[#37699E]', label: t('orders.status.assigned') },
      in_transit: { color: 'bg-[#FFBF07]', label: t('orders.status.inTransit') },
      delivered: { color: 'bg-[#14A38B]', label: t('orders.status.delivered') },
      cancelled: { color: 'bg-[#FF7171]', label: t('orders.status.cancelled') },
    };
    
    const config = statusConfig[status] || { color: 'bg-[#5A6B7A]', label: status };
    return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-semibold text-[#37699E] font-['Lexend_Deca']">{t('admin.adminOrders.title')}</h1>
          <p className="text-sm text-muted-foreground mt-2">{t('admin.adminOrders.subtitle')}</p>
        </div>

        <Card className="p-6">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <SearchInput
              placeholder={t('common.search')}
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
            <Select value={filters.status || undefined} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger>
                <SelectValue placeholder={t('admin.adminOrders.filters.allStatuses')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">{t('orders.status.draft')}</SelectItem>
                <SelectItem value="posted">{t('orders.status.posted')}</SelectItem>
                <SelectItem value="booked">{t('orders.status.booked')}</SelectItem>
                <SelectItem value="assigned">{t('orders.status.assigned')}</SelectItem>
                <SelectItem value="in_transit">{t('orders.status.inTransit')}</SelectItem>
                <SelectItem value="delivered">{t('orders.status.delivered')}</SelectItem>
                <SelectItem value="cancelled">{t('orders.status.cancelled')}</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => setFilters({ status: '', search: '' })}>
              {t('common.reset')}
            </Button>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-16" />)}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('admin.adminOrders.orderId')}</TableHead>
                    <TableHead>{t('admin.adminOrders.shipper')}</TableHead>
                    <TableHead>{t('admin.adminOrders.transporter')}</TableHead>
                    <TableHead>{t('admin.adminOrders.amount')}</TableHead>
                    <TableHead>{t('admin.adminOrders.status')}</TableHead>
                    <TableHead>{t('admin.adminOrders.date')}</TableHead>
                    <TableHead className="text-right">{t('admin.adminOrders.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders?.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell className="font-medium">{order.order_number}</TableCell>
                      <TableCell>{order.shipper_company?.name || '-'}</TableCell>
                      <TableCell>{order.transport_jobs[0]?.transporter_company?.name || '-'}</TableCell>
                      <TableCell>{order.final_price ?? order.proposed_price ? `${order.final_price ?? order.proposed_price} €` : '-'}</TableCell>
                      <TableCell>{getStatusBadge(order.status)}</TableCell>
                      <TableCell>{format(new Date(order.created_at), 'dd/MM/yyyy')}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedOrder(order)}
                          >
                            <Eye size={16} />
                          </Button>
                          {order.status !== 'delivered' && order.status !== 'cancelled' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setActionDialog({ type: 'deliver', order })}
                            >
                              <CheckCircle size={16} className="text-[#14A38B]" />
                            </Button>
                          )}
                          {order.status !== 'delivered' && order.status !== 'cancelled' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setActionDialog({ type: 'cancel', order })}
                            >
                              <XCircle size={16} className="text-[#FF7171]" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      </div>

      {/* Order Details Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('admin.adminOrders.orderDetails.title')}</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-semibold mb-3 text-[#37699E]">{t('admin.adminOrders.orderDetails.route')}</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <p className="text-muted-foreground">{t('orders.pickup')}</p>
                      <p className="font-medium">{selectedOrder.pickup_city}, {selectedOrder.pickup_country}</p>
                      <p className="text-xs text-muted-foreground">{format(new Date(selectedOrder.pickup_date), 'dd/MM/yyyy HH:mm')}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t('orders.delivery')}</p>
                      <p className="font-medium">{selectedOrder.delivery_city}, {selectedOrder.delivery_country}</p>
                      <p className="text-xs text-muted-foreground">{format(new Date(selectedOrder.delivery_date), 'dd/MM/yyyy HH:mm')}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold mb-3 text-[#37699E]">{t('admin.adminOrders.orderDetails.cargo')}</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <p className="text-muted-foreground">{t('orders.weight')}</p>
                      <p className="font-medium">{selectedOrder.weight_kg} kg</p>
                    </div>
                    {selectedOrder.volume_m3 && (
                      <div>
                        <p className="text-muted-foreground">{t('orders.volume')}</p>
                        <p className="font-medium">{selectedOrder.volume_m3} m³</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold mb-3 text-[#37699E]">{t('admin.adminOrders.orderDetails.pricing')}</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">{t('admin.adminOrders.status')}</p>
                    {getStatusBadge(selectedOrder.status)}
                  </div>
                  <div>
                    <p className="text-muted-foreground">{t('admin.adminOrders.amount')}</p>
                    <p className="font-medium text-lg">{selectedOrder.final_price || selectedOrder.proposed_price || 0} €</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <AlertDialog open={!!actionDialog.type} onOpenChange={() => setActionDialog({ type: null, order: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {actionDialog.type === 'deliver' && t('admin.adminOrders.confirmMarkDelivered')}
              {actionDialog.type === 'cancel' && t('admin.adminOrders.confirmCancel')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {actionDialog.type === 'deliver' && t('admin.adminOrders.confirmMarkDeliveredMessage')}
              {actionDialog.type === 'cancel' && t('admin.adminOrders.confirmCancelMessage')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleAction}>{t('common.confirm')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminOrders;
